// The ten steps from sign-up to the first automatic reminder (v2.7 US-3.1).
// Single source for the public /getting-started page and its HowTo JSON-LD.
//
// `checklist` names the matching step of the in-app Getting Started card
// (server/controllers/onboardingController.js computeSteps). The five in-app
// steps must all appear here with the same wording in `title`;
// server/__tests__/gettingStarted.alignment.test.js enforces it (US-3.2).
// `free` marks steps a Free or Free (YMCA) program skips.
import { TRIAL_DAYS, priceLabel } from './pricing';

export const GETTING_STARTED_STEPS = [
  {
    id: 'trial',
    title: 'Start your free trial',
    body: `Sign up at hoophero.net with your name, email and program name. ${TRIAL_DAYS} days free, no credit card needed to start, then ${priceLabel()}.`,
    where: 'hoophero.net → Start free trial',
    path: '/coach/register',
  },
  {
    id: 'program',
    title: 'Pick your program type and season',
    body: 'Choose Paid, Free or Free (YMCA), and the season you are running. Pick your state, then your branch, for a YMCA program. You can change both later.',
    where: 'Sign-up form, later Settings → Program Identity',
    path: '/coach/settings',
  },
  {
    id: 'timezone',
    title: 'Check your time zone',
    body: 'Game reminders go out the day before at 5 pm in your time zone, so make sure it is yours.',
    where: 'Settings → Program Identity → Time Zone',
    path: '/coach/settings',
  },
  {
    id: 'fee',
    checklist: 'fee',
    title: 'Set your monthly fee',
    body: 'The fee per player that parents pay you. HoopHero only tracks it and takes nothing from it. Free programs skip this step.',
    where: 'Settings → Program Identity',
    path: '/coach/settings',
    free: false,
  },
  {
    id: 'methods',
    checklist: 'methods',
    title: 'Tell parents how to pay you',
    body: 'Switch on the ways you accept money, such as Venmo, Zelle, Cash App or cash, so parents see your handles next to their balance. Free programs skip this step.',
    where: 'Settings → How Parents Pay You',
    path: '/coach/settings',
    free: false,
  },
  {
    id: 'venue',
    title: 'Add your gym',
    body: 'Save the gyms you play at once, with the address and a note for parents such as where to park, then pick them when you add a game.',
    where: 'Settings → Venues',
    path: '/coach/settings',
  },
  {
    id: 'game',
    checklist: 'game',
    title: 'Add your first game or practice',
    body: 'Date, time, gym and opponent. Add a weekly practice once and it repeats. Parents see the schedule the moment they join.',
    where: 'Schedule → Add',
    path: '/coach/schedule',
  },
  {
    id: 'waiver',
    title: 'Publish your waiver',
    body: 'Start from the built-in participation waiver, edit it, and publish. Parents sign by typing their name, and the roster shows who has not.',
    where: 'Settings → Waiver',
    path: '/coach/settings',
  },
  {
    id: 'invite',
    checklist: 'invite',
    title: 'Share your invite link',
    body: 'Copy your join link or program code and send it to your parents, or print the one-page parent handout for sign-up night.',
    where: 'Player Sign-Up → Copy Link',
    path: '/coach/signup',
  },
  {
    id: 'family',
    checklist: 'family',
    title: 'Get your first family on the roster',
    body: 'Parents register with your code and add their own children. You can also enroll a family yourself at sign-up night. Then watch the first RSVPs come in, and the day before the game the first reminder goes out on its own.',
    where: 'Player Sign-Up',
    path: '/coach/signup',
  },
];

// schema.org HowTo for the page, built from the same list.
export const gettingStartedHowToJsonLd = () => ({
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name: 'Set up your youth basketball program on HoopHero',
  description: 'Ten steps from sign-up to your first automatic game reminder, about ten minutes in total.',
  totalTime: 'PT10M',
  step: GETTING_STARTED_STEPS.map((s, i) => ({
    '@type': 'HowToStep',
    position: i + 1,
    name: s.title,
    text: s.body,
    url: `https://www.hoophero.net/getting-started#step-${s.id}`,
  })),
});
